import { API_BASE_URL } from "../config";
import { acquireAccessToken, redirectToLogin } from "../helpers/AccessToken";
import apiClient from "./apiClient";

/**
 * Servicio de comunicación con el agente de chat IA
 * Endpoints para conversaciones y mensajes
 */

async function fetchChatStreamWithRetry(url, payload, navigate, signal) {
  const executeRequest = async (forceRefresh = false) => {
    const token = await acquireAccessToken({
      navigate,
      forceRefresh,
      redirectOnFailure: !forceRefresh,
    });

    if (!token) {
      throw new Error("No se pudo obtener el token de acceso.");
    }

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal,
    });
  };

  let response = await executeRequest(false);

  if (response.status === 401) {
    try {
      response = await executeRequest(true);
    } catch {
      await redirectToLogin();
      throw new Error("Sesión expirada.");
    }
  }

  if (response.status === 401) {
    await redirectToLogin();
    throw new Error("Sesión expirada.");
  }

  return response;
}

export const sendChatMessage = async (message, conversationId, navigate) => {
  try {
    const response = await apiClient.post(
      "/chat/message",
      { message, conversationId },
      {
        navigate,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Chat error: ${error.response?.status || 500}`);
  }
};

/**
 * Send a message with streaming (SSE). Returns an async iterator that yields:
 *  { type: "meta", conversationId }
 *  { type: "delta", text: "chunk" }
 *  { type: "done", content: [...parsed blocks] }
 *  { type: "error", message: "..." }
 */
export const sendChatMessageStream = async (message, conversationId, navigate, signal) => {
  const response = await fetchChatStreamWithRetry(
    `${API_BASE_URL}/chat/message/stream`,
    { message, conversationId },
    navigate,
    signal
  );

  if (!response.ok) throw new Error(`Chat error: ${response.status}`);

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  return {
    async *[Symbol.asyncIterator]() {
      let buffer = "";
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6).trim();
            if (!payload || payload === "[DONE]") continue;
            try {
              yield JSON.parse(payload);
            } catch {
              // skip unparseable
            }
          }
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        throw err;
      }
    },
    cancel() {
      reader.cancel();
    },
  };
};

export const getConversations = async (options = {}, navigate) => {
  const { page = 1, limit = 10 } = options;

  try {
    const response = await apiClient.get("/chat/conversations", {
      navigate,
      params: { page, limit },
    });

    const payload = response.data || {};
    const conversations = Array.isArray(payload.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];

    return {
      data: conversations,
      total: Number(payload.total) || conversations.length,
      page: Number(payload.page) || page,
      totalPages: Number(payload.totalPages) || (conversations.length > 0 ? 1 : 0),
    };
  } catch (error) {
    throw new Error(`Error: ${error.response?.status || 500}`);
  }
};

export const getConversation = async (conversationId, navigate) => {
  try {
    const response = await apiClient.get(`/chat/conversations/${conversationId}`, {
      navigate,
    });

    return response.data;
  } catch (error) {
    throw new Error(`Error: ${error.response?.status || 500}`);
  }
};

export const deleteConversation = async (conversationId, navigate) => {
  try {
    const response = await apiClient.delete(`/chat/conversations/${conversationId}`, {
      navigate,
    });

    return response.data;
  } catch (error) {
    throw new Error(`Error: ${error.response?.status || 500}`);
  }
};
